import type { ISO8601 } from '@app/constants/Misc'

import { excludeAbortError } from '@app/errors/AbortError'

import { compact } from '@app/utils/compactV2Entity'
import { createSelector } from '@app/utils/createSelector'
import { mergeGuarded } from '@app/utils/mergeGuarded'
import moment from '@app/utils/moment'

import type { ApiUserProfile } from '@app/packages/api/v2/types/entities'
import type { ApiDataResponse } from '@app/packages/api/v2/types/misc'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'
import type { ProfileUpdateState, StoreParentProfile, StoreProfileUser, StoreSitterProfile, StoreVisitorProfile } from '@app/store/types/profile'

const profileUserSlice = createReduxSlice<StoreProfileUser>('profile_user_model')

const profileMetaSlice = createReduxSlice<ProfileMeta>('profile_meta')

const profileUpdateSlice = createReduxSlice<ProfileUpdateState>('profile_update')

/** The user a profile read returns replaces the profile, keeping the fields a response omits. */
export const setProfileFromUserResponseSlice = (payload: ProfileUserResponse) =>
  createThunk(dispatch => {
    dispatch(profileUserSlice.update(state => foldProfileUser(state, payload)))
    dispatch(profileMetaSlice.update(loadedMeta))
  })

/** A sitter who just asked for training has one, whatever the profile was read as. */
export const markProfileTrainingRequestedSlice = () =>
  profileUserSlice.update(state => (state?.account_type === 'sitter' ? { ...state, has_training_request: true } : state))

export const startProfileUpdateSlice = () => profileMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), error: null, loading: true }))

export const failProfileUpdateSlice = (error: Error) =>
  profileMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), loading: false, error: excludeAbortError(error) }))

type ProfileUserResponse = ApiDataResponse<ApiUserProfile | undefined>

function foldProfileUser(state: StoreProfileUser | undefined, payload: ProfileUserResponse) {
  const user = payload.data
  if (!user) throw new Error('Got not a user on a profile response')

  const included = payload.included || []

  switch (user.attributes.account_type) {
    case 'parent': {
      const o: StoreParentProfile = {
        id: user.id,
        ...user.attributes,
        subscription: (() => {
          const s = included.find(t => t.type === 'subscriptions')
          return s ? compact(s) : null
        })(),
      }
      return state?.account_type === 'parent' ? mergeGuarded(state, o, 'subscription', 'phone') : o
    }
    case 'sitter': {
      const o: StoreSitterProfile = {
        id: user.id,
        ...user.attributes,
        location: (() => {
          const s = included.find(t => t.type === 'locations')
          return s ? compact(s) : null
        })(),
      }
      return state?.account_type === 'sitter' ? mergeGuarded(state, o, 'location', 'phone') : o
    }
    case 'visitor': {
      const o: StoreVisitorProfile = {
        id: user.id,
        ...user.attributes,
      }
      return state?.account_type === 'visitor' ? mergeGuarded(state, o) : o
    }
    default: {
      return state
    }
  }
}

export const setProfileUserSlice = (user: StoreProfileUser) => profileUserSlice.set(user)

/** An edit also stamps the meta as loaded — kept from when one action fed both slices. */
export const editProfileSlice = (patch: ProfileUpdateState) =>
  createThunk(dispatch => {
    dispatch(profileUpdateSlice.update(state => ({ ...state, ...patch })))
    dispatch(profileMetaSlice.update(loadedMeta))
  })

/** The pending profile form does not survive leaving the screen. */
export const clearProfileUpdateSlice = () => profileUpdateSlice.set({})

export const profileUserSelector = createSelector([profileUserSlice.selector], user => user ?? null)
export const profileMetaSelector = createSelector([profileMetaSlice.selector], meta => meta ?? DEFAULT_META)
export const profileUpdateSelector = createSelector([profileUpdateSlice.selector], update => update ?? EMPTY_UPDATE)

type ProfileMeta = {
  loading: boolean
  loadedAt: ISO8601 | null
  error: Error | null
}

const DEFAULT_META: ProfileMeta = { loading: false, loadedAt: null, error: null }

const loadedMeta = (state: ProfileMeta | undefined): ProfileMeta => ({ ...(state ?? DEFAULT_META), loading: false, loadedAt: moment().format(), error: null })

const EMPTY_UPDATE: ProfileUpdateState = {}
